import type { ResultOf } from '@graphql-typed-document-node/core'
import {
  ChevronBottomOutlineIcon,
  CouponOutlineIcon,
  HeartOutlineIcon,
  HelpPathOutlineIcon,
  HistoryOutlineIcon,
  ListOutlineIcon,
  PersonOutlineIcon,
  VacantOutlineIcon,
} from '@ikyucom/design-system-icons-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Tooltip from '@radix-ui/react-tooltip'
import { useCallback, useRef, useState } from 'react'
import type { ComponentType, FC, ReactElement } from 'react'
import Image from '~/components/image/Image'
import { useEnv } from '~/env'
import { ME_FAVORITES_PATH } from '~/features/favorite/list/url'
import { useViewer } from '~/features/viewer/query'
import type { ViewerQuery } from '~/features/viewer/query'
import {
  DESIGN_SYSTEM_ASSETS_ORIGIN,
  LOGIN_SITE,
  MEMBER_RANK_LABEL,
  PAYPAY_IMAGE_URL,
  POINT_RATE_BY_RANK,
} from '~/lib/constants/constants'
import { callLogout } from '~/lib/member/member'
import { money } from '~/lib/money/money'
import {
  getHelpUrl,
  getLoginUrl,
  getMemberRegistrationUrl,
  getMyPageUrl,
  getReservationConfirmUrl,
} from '~/lib/url'
import { useRedirectBackURL } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { hstack, vstack } from '~/styled-system/patterns'
import { HeaderLogo } from '../HeaderLogo'
import { HeaderWrapper } from '../HeaderWrapper'

const styles = sva({
  slots: [
    'root',
    'primaryNavigation',
    'sites',
    'secondaryNavigation',
    'info',
    'guest',
    'member',
    'memberName',
    'memberMenu',
    'memberMenuItems',
    'rewards',
    'labels',
    'rank',
    'vic',
    'yahooLoginNotes',
    'point',
    'memberMenuFooter',
    'icon',
  ],
  base: {
    root: hstack.raw({
      alignItems: 'center',
      paddingTop: '3',
      paddingBottom: '3',
      width: 'content.base',
      margin: 'auto',
      justifyContent: 'space-between',
    }),
    primaryNavigation: hstack.raw({
      alignItems: 'center',
      gap: '6',
    }),
    sites: hstack.raw({
      gap: '6',
    }),
    secondaryNavigation: hstack.raw({
      alignItems: 'center',
      gap: '0',
    }),
    info: hstack.raw({
      alignItems: 'center',
      gap: '4',
      paddingRight: '4',
      borderRight: 'medium',
    }),
    guest: hstack.raw({
      alignItems: 'center',
      gap: '4',
      paddingLeft: '4',
      color: 'tertiary',
    }),
    member: {
      pl: '4',
    },
    memberName: hstack.raw({
      gap: '1',
      color: 'tertiary',
      maxWidth: '[200px]',
      justifyContent: 'end',
    }),
    memberMenu: {
      bg: 'white',
      borderRadius: 'default',
      shadow: 'outline.lg',
    },
    memberMenuItems: vstack.raw({
      gap: '3',
      alignItems: 'start',
      borderBottom: 'medium',
      p: '5',
      fontSize: 'md',
    }),
    rewards: vstack.raw({
      gap: '2',
      alignItems: 'start',
    }),
    labels: vstack.raw({
      gap: '1',
      alignItems: 'start',
    }),
    rank: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '[76px]',
      height: '[18px]',
      fontSize: 'xs',
      color: 'white',
      lineHeight: 'none',
      borderRadius: 'sm',
      cursor: 'pointer',
      '&[data-rank=D]': { bgGradient: 'ikyu.diamond' },
      '&[data-rank=G]': { bgGradient: 'ikyu.gold' },
      '&[data-rank=P]': { bgGradient: 'ikyu.platinum' },
      '&[data-rank=R]': { bgGradient: 'ikyu.regular' },
    },
    vic: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '[76px]',
      height: '[18px]',
      fontFamily: 'serif',
      fontWeight: 'bold',
      fontSize: 'xs',
      color: 'white',
      bgGradient: 'ikyu.vic',
      borderRadius: 'sm',
    },
    yahooLoginNotes: {
      fontSize: 'xs',
      color: 'gray.700',
    },
    point: {
      fontSize: 'xl',
      fontWeight: 'bold',
    },
    memberMenuFooter: vstack.raw({
      gap: '2',
      alignItems: 'start',
      fontSize: 'sm',
      p: '5',
    }),
    icon: {
      color: 'tertiary',
    },
  },
})

export function PcHeader() {
  const env = useEnv()
  const { data: viewerData, refetch } = useViewer()
  const viewer = viewerData ?? { __typename: 'Guest' }
  const classes = styles()
  return (
    <HeaderWrapper>
      <header className={css({ borderBottom: 'medium' })}>
        <div className={classes.root}>
          <div className={classes.primaryNavigation}>
            <HeaderLogo />
            <IkyuOnlyNavigationMenu />
          </div>
          <div className={classes.secondaryNavigation}>
            <div className={classes.info}>
              <IconTooltip
                label="ヘルプ・お問い合わせ"
                href={getHelpUrl(env)}
                Icon={HelpPathOutlineIcon}
              />
              <History />
              <IconTooltip label="お気に入り" href={ME_FAVORITES_PATH} Icon={HeartOutlineIcon} />
              <p>
                <a href={getReservationConfirmUrl(env)}>予約確認</a>
              </p>
            </div>
            {viewer.__typename !== 'Guest' ? (
              <MemberMenu viewer={viewer} refetchViewer={refetch} />
            ) : (
              <GuestMenu />
            )}
          </div>
        </div>
      </header>
    </HeaderWrapper>
  )
}

function IconTooltip(props: {
  label: string
  href: string
  Icon: ComponentType<{ className?: string; 'aria-label'?: string }>
}) {
  const { icon } = styles.raw()
  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={100}>
        <Tooltip.Trigger asChild>
          <a href={props.href}>
            <props.Icon className={css(icon, { fontSize: '[20px]' })} aria-label={props.label} />
          </a>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className={css({ bg: 'background', p: '2', rounded: 'sm' })}
            sideOffset={2}
          >
            <a href={props.href} className={css({ fontSize: 'sm' })}>
              {props.label}
            </a>
            <Tooltip.Arrow className={css({ fill: 'background' })} />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  )
}

const History: FC = import.meta.env.VITE_MODE === 'ppg' ? EmptyHistory : IkyuHistory

function IkyuHistory() {
  return (
    <IconTooltip
      label="最近見たレストラン"
      href="/rsMain/rsRstViewList.asp"
      Icon={HistoryOutlineIcon}
    />
  )
}

function EmptyHistory() {
  return null
}

const IkyuOnlyNavigationMenu: FC =
  import.meta.env.VITE_MODE === 'ppg' ? EmptyNavigationMenu : NavigationMenu

function EmptyNavigationMenu() {
  return null
}

function NavigationMenu() {
  const env = useEnv()
  const { STAY_IKYU_ORIGIN, SPA_IKYU_ORIGIN, OTORIYOSE_IKYU_ORIGIN, FURUSATO_IKYU_ORIGIN } = env
  const classes = styles()
  return (
    <nav>
      <div className={classes.sites}>
        <div>
          <a href={STAY_IKYU_ORIGIN}>国内宿泊予約</a>
        </div>
        <div>
          <a href={SPA_IKYU_ORIGIN}>スパ予約</a>
        </div>
        <div>
          <a href={`${STAY_IKYU_ORIGIN}/global`}>海外宿泊予約</a>
        </div>
        <div>
          <a href={OTORIYOSE_IKYU_ORIGIN}>お取り寄せ</a>
        </div>
        <div>
          <a href={FURUSATO_IKYU_ORIGIN}>ふるさと納税</a>
        </div>
      </div>
    </nav>
  )
}

type MemberMenuProps = {
  viewer: Exclude<ResultOf<typeof ViewerQuery>['viewer'], { __typename: 'Guest' }>
  refetchViewer: () => Promise<{}>
}

function MemberMenu({ viewer, refetchViewer }: MemberMenuProps) {
  const env = useEnv()
  const redirectBackUrl = useRedirectBackURL()
  const timeout = useRef<NodeJS.Timeout | undefined>(undefined)
  const [open, setOpen] = useState(false)

  const onMouseEnter = useCallback(() => {
    // trigger から dropdown にカーソルを動かしたときに
    // dropdown の mouseenter で trigger の mouseleave を無効化
    clearTimeout(timeout.current)
    setOpen(true)
    // Radix UI によって body で pointer-events が無効化されるのを抑止
    const interval = setInterval(() => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = ''
        clearInterval(interval)
      }
    }, 10)
  }, [])

  const onMouseLeave = useCallback(() => {
    if (document.body.style.pointerEvents === 'none') {
      return
    }
    // trigger から dropdown にカーソルを動かしたときに mouseleave で消えないように遅延させる
    timeout.current = setTimeout(() => {
      setOpen(false)
    }, 200)
  }, [])

  const classes = styles()
  return (
    <div className={classes.member}>
      <DropdownMenu.Root open={open}>
        <DropdownMenu.Trigger asChild>
          <a
            href={getMyPageUrl(env, redirectBackUrl)}
            className={classes.memberName}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <PpgOnlyLypLogo viewer={viewer} />
            {/* viewer.nameが空文字のときは「未設定さん」と表示する */}
            <p className={css({ truncate: true })}>{viewer.name || '未設定'}</p>
            <span className={css({ flexShrink: '[0]' })}>さん</span>
            <ChevronBottomOutlineIcon
              width={12}
              height={12}
              className={css({ flexShrink: '[0]' })}
            />
          </a>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            className={classes.memberMenu}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <DropdownMenu.Arrow width={12} height={6} className={css({ fill: 'white' })} />
            <MemberMenuContent viewer={viewer} refetchViewer={refetchViewer} />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  )
}

const PpgOnlyLypLogo: FC<Pick<MemberMenuProps, 'viewer'>> =
  import.meta.env.VITE_MODE === 'ppg' ? LypLogo : EmptyLogo

function LypLogo({ viewer }: Pick<MemberMenuProps, 'viewer'>) {
  if (viewer.__typename !== 'YahooMember' || !viewer.isPremium) {
    return null
  }
  return (
    <Image
      src={`${DESIGN_SYSTEM_ASSETS_ORIGIN}/design-system/assets/brand-icons/svg/lyp.svg`}
      alt=""
      width={12}
      height={12}
    />
  )
}

function EmptyLogo() {
  return null
}

const MemberMenuContent: FC<MemberMenuProps> =
  import.meta.env.VITE_MODE === 'ppg' ? PpgMemberMenuContent : IkyuMemberMenuContent

function PpgMemberMenuContent() {
  const env = useEnv()
  const redirectBackUrl = useRedirectBackURL()
  const classes = styles()
  return (
    <>
      <DropdownMenu.Group className={classes.memberMenuItems}>
        <DropdownMenu.Item asChild>
          <MenuItem href={getReservationConfirmUrl(env)}>
            <ListOutlineIcon width={18} height={18} className={classes.icon} />
            <span>予約確認・変更</span>
          </MenuItem>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <MenuItem href={ME_FAVORITES_PATH}>
            <HeartOutlineIcon width={18} height={18} className={classes.icon} />
            <span>お気に入り</span>
          </MenuItem>
        </DropdownMenu.Item>
      </DropdownMenu.Group>
      <DropdownMenu.Separator />
      <DropdownMenu.Group className={classes.memberMenuFooter}>
        <DropdownMenu.Item>
          <a
            href={getHelpUrl(env)}
            className={css({ _hover: { color: 'tertiary' } })}
            target="_blank"
            rel="noreferrer"
          >
            ヘルプ・お問い合わせ
          </a>
        </DropdownMenu.Item>
        <DropdownMenu.Item>
          <a
            href={getMyPageUrl(env, redirectBackUrl)}
            className={css({ cursor: 'pointer', _hover: { color: 'tertiary' } })}
          >
            Yahoo! JAPAN ID登録情報
          </a>
        </DropdownMenu.Item>
      </DropdownMenu.Group>
    </>
  )
}

function IkyuMemberMenuContent({ viewer, refetchViewer }: MemberMenuProps) {
  const env = useEnv()

  const handleLogout = useCallback(async () => {
    await callLogout(env.MEMBER_ORIGIN)
    await refetchViewer()
  }, [env.MEMBER_ORIGIN, refetchViewer])

  if (viewer.__typename !== 'Member') {
    return null
  }

  const classes = styles()
  return (
    <>
      <DropdownMenu.Group className={classes.memberMenuItems}>
        <DropdownMenu.Item asChild>
          <div className={classes.rewards}>
            <YahooLoginInfo viewer={viewer} />
            <MemberRankInfo viewer={viewer} />
          </div>
        </DropdownMenu.Item>
      </DropdownMenu.Group>
      <DropdownMenu.Separator />
      <DropdownMenu.Group className={classes.memberMenuItems}>
        <DropdownMenu.Item asChild>
          <MenuItem href={getMyPageUrl(env, '')}>
            <PersonOutlineIcon width={18} height={18} className={classes.icon} />
            <span>マイページ</span>
          </MenuItem>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <MenuItem href={getReservationConfirmUrl(env)}>
            <ListOutlineIcon width={18} height={18} className={classes.icon} />
            <span>予約確認・変更</span>
          </MenuItem>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <MenuItem href="/rsMain/rsRstViewList.asp">
            <HistoryOutlineIcon width={18} height={18} className={classes.icon} />
            <span>最近見たレストラン</span>
          </MenuItem>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <MenuItem href={ME_FAVORITES_PATH}>
            <HeartOutlineIcon width={18} height={18} className={classes.icon} />
            <span>お気に入り</span>
          </MenuItem>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <MenuItem href="/rsMain/rsVacancyNoticeList.asp">
            <VacantOutlineIcon width={18} height={18} className={classes.icon} />
            <span>空席通知</span>
          </MenuItem>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <MenuItem href={`${env.MEMBER_ORIGIN}/member/coupon/?service=RS`}>
            <CouponOutlineIcon width={18} height={18} className={classes.icon} />
            <span>獲得済みクーポン</span>
          </MenuItem>
        </DropdownMenu.Item>
      </DropdownMenu.Group>
      <DropdownMenu.Separator />
      <DropdownMenu.Group className={classes.memberMenuFooter}>
        <DropdownMenu.Item>
          <a
            href={getHelpUrl(env)}
            className={css({ _hover: { color: 'tertiary' } })}
            target="_blank"
            rel="noreferrer"
          >
            ヘルプ・お問い合わせ
          </a>
        </DropdownMenu.Item>
        {viewer && (
          <DropdownMenu.Item>
            <span
              onClick={handleLogout}
              className={css({ cursor: 'pointer', _hover: { color: 'tertiary' } })}
            >
              ログアウト
            </span>
          </DropdownMenu.Item>
        )}
      </DropdownMenu.Group>
    </>
  )
}

function YahooLoginInfo({
  viewer,
}: { viewer: Extract<ResultOf<typeof ViewerQuery>['viewer'], { __typename: 'Member' }> }) {
  if (viewer.loginSite !== LOGIN_SITE.Yahoo) {
    return null
  }

  const rank = viewer.rank as keyof typeof MEMBER_RANK_LABEL | undefined
  const pointRate = rank ? POINT_RATE_BY_RANK[rank] : undefined
  const classes = styles()
  return (
    <div className={css({ spaceY: '1' })}>
      <p className={classes.yahooLoginNotes}>Yahoo! JAPAN IDでログインしています</p>
      <div className={hstack({ gap: '2' })}>
        <p className={css({ fontSize: 'sm' })}>あなたの獲得率</p>
        <div className={hstack({ gap: '1' })}>
          <Image src={PAYPAY_IMAGE_URL} width={14} height={14} alt="paypay" />
          {/* TODO: colors.accent.redとしてトークン化する */}
          <span className={css({ fontWeight: 'bold', color: '[#f50000]' })}>{`${pointRate}%`}</span>
        </div>
      </div>
    </div>
  )
}

function MemberRankInfo({
  viewer,
}: { viewer: Extract<ResultOf<typeof ViewerQuery>['viewer'], { __typename: 'Member' }> }) {
  const env = useEnv()

  if (!viewer.pointVisible && !viewer.isVicSalonMember) {
    return null
  }

  const rank = viewer.rank as keyof typeof MEMBER_RANK_LABEL | undefined
  const rankText = rank ? MEMBER_RANK_LABEL[rank] : undefined
  const classes = styles()
  return (
    <>
      <div className={classes.labels}>
        {viewer.pointVisible && (
          <a href="https://my.ikyu.com/membership/" className={classes.rank} data-rank={rank}>
            {rankText}
          </a>
        )}
        {viewer.isVicSalonMember && (
          <a href={`${env.STAY_IKYU_ORIGIN}/vic-salon/`} className={classes.vic}>
            VIC Salon
          </a>
        )}
      </div>
      {viewer.pointVisible && viewer.loginSite !== LOGIN_SITE.Yahoo && (
        <a href="https://my.ikyu.com/member/point/history/" className={css({ color: 'tertiary' })}>
          <span className={classes.point}>{money(viewer.point)}</span>ポイント
        </a>
      )}
    </>
  )
}

type MenuItemProps = {
  children: ReactElement | ReactElement[]
  href: string
}

function MenuItem({ children, href }: MenuItemProps) {
  return (
    <a
      href={href}
      className={hstack({ gap: '2', alignItems: 'center', _hover: { color: 'tertiary' } })}
    >
      {children}
    </a>
  )
}

const GuestMenu: FC = import.meta.env.VITE_MODE === 'ppg' ? PpgGuestMenu : IkyuGuestMenu

function PpgGuestMenu() {
  const env = useEnv()
  const redirectBackUrl = useRedirectBackURL()
  const classes = styles()
  return (
    <div className={classes.guest}>
      <a href={getMemberRegistrationUrl(env, redirectBackUrl)}>ID新規取得</a>
      <a href={getLoginUrl(env, redirectBackUrl)}>ログイン</a>
    </div>
  )
}

function IkyuGuestMenu() {
  const env = useEnv()
  const redirectBackUrl = useRedirectBackURL()
  const classes = styles()
  return (
    <div className={classes.guest}>
      <a href={getMemberRegistrationUrl(env, redirectBackUrl)}>会員登録</a>
      <a href={getLoginUrl(env, redirectBackUrl)}>ログイン</a>
    </div>
  )
}
